/**
 * Viewport Engine - eventos de visibilidade e interação de elementos (comportamental).
 * Dispara ELEMENT_* via Queue (enqueueEvent, prioridade low).
 */

import type { GomarkePixelConfig } from '../types';
import type { ViewportConfig } from './types';
import { getSession, getVisitor } from '../core';
import { enqueueEvent } from '../core/queue';
import { shouldSampleEvent } from '../core/sampling/dynamicSampling';
import { getUnifiedPressureLevel } from '../core/runtime/pressureModel';

function buildSelector(
  selector: string,
  selectorType: ViewportConfig['selectorType']
): string {
  switch (selectorType) {
    case 'class':
      return `.${selector}`;
    case 'id':
      return `#${selector}`;
    case 'attribute':
      return `[${selector}]`;
    case 'data-attribute':
      return `[data-${selector}]`;
    case 'tag':
    case 'custom':
    default:
      return selector;
  }
}

function findElements(
  selector: string,
  selectorType: ViewportConfig['selectorType']
): Element[] {
  const full = buildSelector(selector, selectorType);
  try {
    return Array.from(document.querySelectorAll(full));
  } catch {
    return [];
  }
}

function getElementPosition(el: Element): {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
} {
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    bottom: rect.bottom + window.scrollY,
    right: rect.right + window.scrollX,
    width: rect.width,
    height: rect.height,
  };
}

function isElementVisible(el: Element): boolean {
  const style = window.getComputedStyle(el);
  const html = el as HTMLElement;
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    style.opacity !== '0' &&
    html.offsetWidth > 0 &&
    html.offsetHeight > 0
  );
}

function isElementInViewport(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight ?? document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth ?? document.documentElement.clientWidth)
  );
}

function extractElementData(el: Element): {
  tagName: string;
  id?: string;
  classes: string[];
  position: ReturnType<typeof getElementPosition>;
  isVisible: boolean;
  isInViewport: boolean;
} {
  const classes =
    el.className && typeof el.className === 'string'
      ? el.className.trim().split(/\s+/)
      : [];
  return {
    tagName: el.tagName.toLowerCase(),
    id: el.id || undefined,
    classes,
    position: getElementPosition(el),
    isVisible: isElementVisible(el),
    isInViewport: isElementInViewport(el),
  };
}

/** Dados completos do elemento para PAGE_CLICK (mesmo formato que element_data em ELEMENT_CLICK). */
export function getElementDataForClick(el: Element): Record<string, unknown> {
  return extractElementData(el);
}

interface TrackedElement {
  key: string;
  element: Element;
  selector: string;
  isVisible: boolean;
  visibilityStartTime?: number;
  totalVisibleTime: number;
  clickCount: number;
  hoverCount: number;
  lastIntersectionRatio: number;
}

let viewportConfig: ViewportConfig | null = null;
let engineConfig: { config: GomarkePixelConfig; version: string } | null = null;
const trackedElements = new Map<string, TrackedElement>();
const trackedElementKeys = new WeakMap<Element, string>();
let intersectionObserver: IntersectionObserver | null = null;
let visibilityObserver: IntersectionObserver | null = null;
const eventListeners = new Map<string, (e: Event) => void>();

function getVersion(): string {
  return engineConfig?.version ?? '1.0.0';
}

function sendViewportEvent(
  type:
    | 'ELEMENT_ENTER'
    | 'ELEMENT_EXIT'
    | 'ELEMENT_VISIBLE'
    | 'ELEMENT_HIDDEN'
    | 'ELEMENT_CLICK'
    | 'ELEMENT_HOVER',
  eventData: Record<string, unknown>
): void {
  if (getUnifiedPressureLevel() > 0.7 && !shouldSampleEvent(0.35)) return;
  const cfg = engineConfig?.config;
  const session = getSession();
  const visitor = getVisitor();
  if (!cfg || !session || !visitor) return;
  const params = {
    category: 'ENGAGEMENT' as const,
    type,
    config: cfg,
    sessionId: session.sessionId,
    visitorId: visitor.visitorId,
    version: getVersion(),
    eventData: {
      ...eventData,
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: Date.now(),
    },
  };
  enqueueEvent(params, 'low');
}

const DATA_VIEWPORT_KEY = 'data-viewport-key';

function getElementKey(element: Element, index?: number): string {
  const pos = getElementPosition(element);
  return `element_${element.tagName.toLowerCase()}_${Math.floor(pos.top)}_${Math.floor(pos.left)}_${index ?? 0}`;
}

/** Descritor serializável de um EventTarget (evita enviar objetos DOM no payload). Exportado para PAGE_CLICK e outros. */
export function eventTargetDescriptor(
  target: EventTarget | null
): Record<string, unknown> {
  if (!target) return { _: 'null' };
  if (target instanceof Document)
    return { _: 'document', nodeName: '#document' };
  if (typeof window !== 'undefined' && target === window)
    return { _: 'window' };
  if (!(target instanceof Element)) return { _: 'non-element' };
  const el = target as Element;
  const classes =
    el.className && typeof el.className === 'string'
      ? el.className.trim().split(/\s+/).slice(0, 5)
      : [];
  const desc: Record<string, unknown> = {
    tagName: el.tagName.toLowerCase(),
    id: el.id || undefined,
    classes,
    type: (el as HTMLInputElement).type || undefined,
  };
  if (el === document.documentElement) desc._root = true;
  return desc;
}

/** Extrai posição do clique de um MouseEvent para enriquecer payload. */
export function getClickPosition(
  event: Event
): Record<string, number> | undefined {
  if (!(event instanceof MouseEvent)) return undefined;
  return {
    client_x: event.clientX,
    client_y: event.clientY,
    page_x: event.pageX,
    page_y: event.pageY,
    offset_x: event.offsetX,
    offset_y: event.offsetY,
  };
}

function handleIntersectionChange(entry: IntersectionObserverEntry): void {
  const el = entry.target as Element;
  const key = trackedElementKeys.get(el) ?? el.getAttribute(DATA_VIEWPORT_KEY);
  if (!key) return;
  const tracked = trackedElements.get(key);
  if (!tracked) return;
  tracked.lastIntersectionRatio = entry.intersectionRatio;
  if (entry.isIntersecting) {
    sendViewportEvent('ELEMENT_ENTER', {
      element_selector: tracked.selector,
      element_tag: (entry.target as Element).tagName.toLowerCase(),
      intersection_ratio: tracked.lastIntersectionRatio,
      element_position: getElementPosition(entry.target as Element),
      element_visible: isElementVisible(entry.target as Element),
      element_in_viewport: isElementInViewport(entry.target as Element),
    });
  } else {
    sendViewportEvent('ELEMENT_EXIT', {
      element_selector: tracked.selector,
      element_tag: (entry.target as Element).tagName.toLowerCase(),
      intersection_ratio: tracked.lastIntersectionRatio,
      total_visible_time: tracked.totalVisibleTime,
      click_count: tracked.clickCount,
      hover_count: tracked.hoverCount,
    });
  }
}

function handleVisibilityChange(entry: IntersectionObserverEntry): void {
  const el = entry.target as Element;
  const key = trackedElementKeys.get(el) ?? el.getAttribute(DATA_VIEWPORT_KEY);
  if (!key) return;
  const tracked = trackedElements.get(key);
  if (!tracked) return;
  const wasVisible = tracked.isVisible;
  tracked.isVisible = entry.isIntersecting;
  if (!wasVisible && tracked.isVisible) {
    tracked.visibilityStartTime = Date.now();
    sendViewportEvent('ELEMENT_VISIBLE', {
      element_selector: tracked.selector,
      element_tag: (entry.target as Element).tagName.toLowerCase(),
      visibility_threshold: viewportConfig?.visibilityThreshold ?? 0.5,
      element_data: extractElementData(entry.target as Element),
    });
  } else if (wasVisible && !tracked.isVisible) {
    const visibilityDuration = tracked.visibilityStartTime
      ? Date.now() - tracked.visibilityStartTime
      : 0;
    if (tracked.visibilityStartTime) {
      tracked.totalVisibleTime += visibilityDuration;
      tracked.visibilityStartTime = undefined;
    }
    sendViewportEvent('ELEMENT_HIDDEN', {
      element_selector: tracked.selector,
      element_tag: (entry.target as Element).tagName.toLowerCase(),
      total_visible_time: tracked.totalVisibleTime,
      visibility_duration: visibilityDuration,
    });
  }
}

function trackElement(el: Element, key: string): void {
  const cfg = viewportConfig;
  if (!cfg) return;
  const tracked: TrackedElement = {
    key,
    element: el,
    selector: cfg.selector,
    isVisible: false,
    totalVisibleTime: 0,
    clickCount: 0,
    hoverCount: 0,
    lastIntersectionRatio: 0,
  };
  trackedElements.set(key, tracked);
  trackedElementKeys.set(el, key);
  if (intersectionObserver) intersectionObserver.observe(el);
  if (visibilityObserver) visibilityObserver.observe(el);
  if (cfg.trackElementClicks) {
    const clickListener = (event: Event) => {
      tracked.clickCount++;
      const clickPosition = getClickPosition(event);
      sendViewportEvent('ELEMENT_CLICK', {
        element_selector: tracked.selector,
        element_tag: el.tagName.toLowerCase(),
        click_count: tracked.clickCount,
        total_visible_time: tracked.totalVisibleTime,
        element_data: extractElementData(el),
        ...(clickPosition && { ...clickPosition }),
        click_event: {
          type: event.type,
          target_desc: eventTargetDescriptor(event.target),
          current_target_desc: eventTargetDescriptor(event.currentTarget),
          ...(clickPosition && { ...clickPosition }),
        },
      });
    };
    el.addEventListener('click', clickListener);
    eventListeners.set(`click_${key}`, clickListener);
  }
  if (cfg.trackElementHovers) {
    const hoverListener = () => {
      tracked.hoverCount++;
      sendViewportEvent('ELEMENT_HOVER', {
        element_selector: tracked.selector,
        element_tag: el.tagName.toLowerCase(),
        hover_count: tracked.hoverCount,
        total_visible_time: tracked.totalVisibleTime,
        element_data: extractElementData(el),
      });
    };
    el.addEventListener('mouseenter', hoverListener);
    eventListeners.set(`hover_${key}`, hoverListener);
  }
}

function discoverAndTrackElements(): void {
  const cfg = viewportConfig;
  if (!cfg || !engineConfig) return;
  const maxEl = cfg.maxTrackingElements ?? 50;
  const elements = findElements(cfg.selector, cfg.selectorType);
  elements.forEach((el, index) => {
    if (trackedElements.size >= maxEl) return;
    if (trackedElementKeys.has(el)) return;
    const key = getElementKey(el, index);
    trackElement(el, key);
  });
}

export function initViewportBehavior(
  config: GomarkePixelConfig,
  version: string,
  options: ViewportConfig
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (options.enabled === false || !options.selector) return;
  viewportConfig = options;
  engineConfig = { config, version };

  if (options.trackElementIntersection !== false) {
    intersectionObserver = new IntersectionObserver(
      entries => entries.forEach(handleIntersectionChange),
      { threshold: options.intersectionThreshold ?? 0.1, rootMargin: '0px' }
    );
  }
  if (options.trackElementVisibility !== false) {
    visibilityObserver = new IntersectionObserver(
      entries => entries.forEach(handleVisibilityChange),
      { threshold: options.visibilityThreshold ?? 0.5, rootMargin: '0px' }
    );
  }

  const runDiscovery = (): void => {
    discoverAndTrackElements();
    if (trackedElements.size === 0 && document.readyState !== 'complete') {
      document.addEventListener(
        'DOMContentLoaded',
        () => discoverAndTrackElements(),
        { once: true }
      );
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runDiscovery, { once: true });
  } else {
    setTimeout(runDiscovery, 0);
  }
}

export function getViewportState(): {
  trackedCount: number;
  visibleCount: number;
} {
  const visible = Array.from(trackedElements.values()).filter(
    t => t.isVisible
  ).length;
  return { trackedCount: trackedElements.size, visibleCount: visible };
}

export function destroyViewportBehavior(): void {
  trackedElements.forEach(t => {
    if (t.element.getAttribute(DATA_VIEWPORT_KEY) === t.key) {
      t.element.removeAttribute(DATA_VIEWPORT_KEY);
    }
  });
  eventListeners.forEach((fn, listenerKey) => {
    const elementKey = listenerKey.replace(/^(click|hover)_/, '');
    const tracked = trackedElements.get(elementKey);
    if (tracked) {
      if (listenerKey.startsWith('click_'))
        tracked.element.removeEventListener('click', fn);
      else tracked.element.removeEventListener('mouseenter', fn);
    }
  });
  eventListeners.clear();
  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }
  if (visibilityObserver) {
    visibilityObserver.disconnect();
    visibilityObserver = null;
  }
  trackedElements.clear();
  viewportConfig = null;
  engineConfig = null;
}
